import { useState } from 'react'
import styled from 'styled-components'

import {
  useUserProfileSectionsQuery,
  PaginateUserStoriesDocument,
  PaginateUserListingsDocument,
  PaginateUserReviewsDocument,
} from '../../hooks/useQueries'
import profileSectionsVariables from './profileSectionsVariables'

import StoryCard from '../../components/cards/StoryCard'
import { Content } from '../../components/cards/shared/styled'
import ListingCard from '../../components/cards/ListingCard'
import Review from '../../components/cards/shared/Review'
import Button from '../../components/display/Button'
import Spinner from '../../components/display/Spinner'

const Section = styled.section`
  padding: 40px 0;
  margin: 0 !important;
`

const SectionTitle = styled.h2`
  margin: 0;
  margin-bottom: 40px;
  font-size: 28px;
  line-height: 1;
`

const Counter = styled.span`
  position: relative;
  top: -4px;
  display: inline-block;
  width: 24px;
  height: 24px;
  margin-left: 12px;

  font-size: 16px;
  font-weight: normal;
  line-height: 24px;
  text-align: center;

  border-radius: 4px;
  color: white;
  background-color: ${({ theme }) => theme.brand.jungle};
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const CardWrapper = styled.div`
  position: relative;

  ${Content} {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
`

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`

// Each section paginates with its own document so "Show More" fetches only
// that section (not all three); the result merges into the same connection the
// UserProfileSections query reads, via relayStylePagination. Page size is the
// document's default $first.
const SECTION_DOCUMENT = {
  stories: PaginateUserStoriesDocument,
  listings: PaginateUserListingsDocument,
  reviews: PaginateUserReviewsDocument,
}

const ProfileSections = ({ userId }) => {
  const [fetchingMore, setFetchingMore] = useState({})
  // cache-first reads the SSR seed on the server and on the first client render
  // (loading=false), so the first page is in the server HTML without a
  // hydration mismatch.
  const { data, fetchMore } = useUserProfileSectionsQuery({
    variables: profileSectionsVariables(userId),
    fetchPolicy: 'cache-first',
  })

  const user = data?.user
  if (!user) return null

  const handleLoadMore = (section) => () => {
    const { endCursor } = user[section].pageInfo
    if (!endCursor || fetchingMore[section]) return

    setFetchingMore((state) => ({ ...state, [section]: true }))
    fetchMore({
      query: SECTION_DOCUMENT[section],
      variables: { id: userId, after: endCursor },
    })
      // Swallow fetch failures: the section keeps its current page and the
      // button returns so the user can retry.
      .catch(() => {})
      .finally(() =>
        setFetchingMore((state) => ({ ...state, [section]: false })),
      )
  }

  const renderLoadMore = (section) =>
    user[section].pageInfo.hasNextPage && (
      <LoadMore>
        {fetchingMore[section] ? (
          <Spinner />
        ) : (
          <Button
            type="button"
            onClick={handleLoadMore(section)}
            uppercase={false}
          >
            Show More
          </Button>
        )}
      </LoadMore>
    )

  const { stories, listings, reviews } = user

  return (
    <>
      {stories.totalCount > 0 && (
        <Section>
          <SectionTitle>
            Stories <Counter>{stories.totalCount}</Counter>
          </SectionTitle>
          <Row>
            {stories.edges.map(({ node }) => (
              <CardWrapper
                key={node.id}
                className="col-xs-12 col-sm-6 col-md-4"
              >
                <StoryCard
                  story={node}
                  listing={node.featuredListing}
                  showBadge={false}
                />
              </CardWrapper>
            ))}
          </Row>
          {renderLoadMore('stories')}
        </Section>
      )}

      {listings.totalCount > 0 && (
        <Section>
          <SectionTitle>
            Listings <Counter>{listings.totalCount}</Counter>
          </SectionTitle>
          <Row>
            {listings.edges.map(({ node }) => (
              <CardWrapper key={node.id} className="col-sm-6 col-md-4">
                <ListingCard listing={node} />
              </CardWrapper>
            ))}
          </Row>
          {renderLoadMore('listings')}
        </Section>
      )}

      {reviews.totalCount > 0 && (
        <Section>
          <SectionTitle>
            Reviews <Counter>{reviews.totalCount}</Counter>
          </SectionTitle>
          <div>
            {reviews.edges.map(({ node }) => (
              <Review key={node.id} review={node} />
            ))}
          </div>
          {renderLoadMore('reviews')}
        </Section>
      )}
    </>
  )
}

export default ProfileSections
